// components/portal/RecommendedContactCard.tsx
'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Linkedin, Loader2, Mail, Phone } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { CONTACT_LOOKUP_PROGRESS_EVENT } from '#lib/socket-events';
import type { FullEnrichProgress } from '#lib/leasing/fullenrich';
import type { LeasingBrandDTO } from '#types/types';

// What the user reads while the lookup runs, by how long it has run; FullEnrich's raw state stays
// in the tooltip for whoever debugs it.
const lookupStep = (elapsedMs: number, started: boolean, fr: boolean) => {
  if (!started) return fr ? 'Lancement de la recherche' : 'Starting the search';
  if (elapsedMs < 20_000) return fr ? 'Interrogation de 15+ sources' : 'Asking 15+ phone sources';
  if (elapsedMs < 60_000) return fr ? 'Vérification des annuaires mobiles' : 'Checking mobile directories';
  if (elapsedMs < 120_000) return fr ? 'Recoupement des résultats' : 'Cross-checking the results';
  return fr ? 'Quelques sources lentes restent' : 'A few slow sources left';
};
const clock = (ms: number) => `${Math.floor(ms / 60_000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;

// THE ONE PERSON TO CALL (owner, 2026-10-01: "one precise person that we recommend to contact, a big
// profile picture and some quick information"). The pick is Fater's (scripts/brands/recommend-
// contacts.ts), read from the brand detail; the person is the Contact record itself.
// compact: the same person on the brand's Overview (owner, 2026-10-04: 'the main contact clearly
// identified in the sourcing interface, not a basic list'), one line that opens the Contacts tab.
/** The compact card's shape, shimmering, while the person is on the way. */
export const ContactCardLoading = () => (
  <div className="skeleton-in flex items-center gap-3 rounded-[16px] border border-[#262626] bg-[#1B1B1B] p-3" aria-busy="true" aria-label="Loading" role="status">
    <div className="skeleton h-12 w-12 shrink-0 rounded-full" />
    <div className="min-w-0 flex-1 space-y-2">
      <div className="skeleton h-2.5 w-28 rounded-full opacity-60" />
      <div className="skeleton h-3.5 w-40 rounded-full" />
      <div className="skeleton h-2.5 w-32 rounded-full opacity-60" />
    </div>
  </div>
);

export const RecommendedContactCard = ({ recommendation, lang, compact = false, onOpen }: { recommendation: NonNullable<LeasingBrandDTO['recommendedContact']>; lang: 'en' | 'fr'; compact?: boolean; onOpen?: () => void }) => {
  const { kernel } = useKernel();
  const contact = useKernelStore((state) => state.inventory.Contact.registry[recommendation.contactId]);
  useEffect(() => {
    void kernel.current?.ensureInventoryIds('Contact', [recommendation.contactId]);
  }, [recommendation.contactId, kernel]);
  // THE PHONE IS A CLICK, NEVER AUTOMATIC (owner, 2026-10-04/05: phones cost more credits, keep them
  // manual). The same FullEnrich lookup as the contacts list; it takes 30 s to 3 min, so the button
  // says so from the first frame and cannot be pressed twice.
  const [findingPhone, setFindingPhone] = useState(false);
  const [phoneMissed, setPhoneMissed] = useState(false);
  // LIVE STEPS (owner, 2026-10-05: 'a real telemetry saying each step ... instead of waiting
  // infinitely'). The server reports every poll; a card opened mid-lookup picks it up from the next
  // one, and a clock ticks between them.
  const [startedAt, setStartedAt] = useState(0);
  const [lookup, setLookup] = useState<(FullEnrichProgress & { at: number }) | null>(null);
  const [now, setNow] = useState(0);
  useEffect(() => kernel.current?.subscribeToSocketEvent(CONTACT_LOOKUP_PROGRESS_EVENT, (payload: unknown) => {
    const progress = payload as (FullEnrichProgress & { contactId?: string }) | null;
    if (progress?.contactId === recommendation.contactId) setLookup({ ...progress, at: Date.now() });
  }), [kernel, recommendation.contactId]);
  const searching = findingPhone || Boolean(lookup && now - lookup.at < 15_000);
  useEffect(() => {
    if (!searching) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [searching]);
  const findPhone = useCallback(async () => {
    const k = kernel.current;
    if (!k || !contact || findingPhone) return;
    setFindingPhone(true);
    setPhoneMissed(false);
    setLookup(null);
    setStartedAt(Date.now());
    setNow(Date.now());
    try {
      await k.enrichContact(contact.id, contact.parentType === 'brand' ? contact.parentId : undefined);
      const after = k.getState().inventory.Contact.registry[contact.id];
      if (!after?.directPhone && !after?.primaryPhone) setPhoneMissed(true);
    } catch (error) {
      k.handleError(error instanceof Error ? error.message : 'The phone lookup failed.');
    } finally {
      setFindingPhone(false);
      setLookup(null);
    }
  }, [kernel, contact, findingPhone]);
  const [photoLoaded, setPhotoLoaded] = useState(false);
  if (!contact) return compact ? <ContactCardLoading /> : null;
  const fr = lang === 'fr';
  const name = contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  const phone = contact.directPhone || contact.primaryPhone;
  const elapsed = lookup ? lookup.elapsedMs + Math.max(0, now - lookup.at) : Math.max(0, now - startedAt);
  const stepText = `${lookupStep(elapsed, Boolean(lookup), fr)} · ${clock(elapsed)}`;
  const stepDebug = lookup ? `FullEnrich ${lookup.providerStatus ?? 'submitted'} · poll ${lookup.attempt} · ${Math.round(lookup.elapsedMs / 1000)} s · ${lookup.enrichmentId}` : 'Waiting for the first answer from the server';
  const link = 'inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] px-3 py-1.5 text-[#ECECEC] transition-colors hover:bg-white/[0.12]';
  const avatar = (size: number, fontSize: number) => (
    <div className={`shrink-0 overflow-hidden rounded-full ${contact.photoUrl && !photoLoaded ? 'skeleton' : 'bg-white/[0.08]'}`} style={{ width: size, height: size }}>
      {contact.photoUrl
        ? <img src={contact.photoUrl} alt={name} onLoad={() => setPhotoLoaded(true)} onError={() => setPhotoLoaded(true)}
            className={`h-full w-full object-cover transition-opacity duration-300 ${photoLoaded ? 'opacity-100' : 'opacity-0'}`} />
        : <span className="flex h-full w-full items-center justify-center text-[#8E8E8E]" style={{ fontSize }}>{name.slice(0, 1).toUpperCase()}</span>}
    </div>
  );
  if (compact) {
    const icon = 'flex h-8 w-8 items-center justify-center rounded-full text-[#AFAFAF] transition-colors hover:bg-white/[0.08] hover:text-white';
    return (
      <section aria-label={fr ? 'Contact recommandé' : 'Recommended contact'} className="flex items-center gap-3 rounded-[16px] border border-[#262626] bg-[#1B1B1B] p-3">
        <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          {avatar(48, 18)}
          <span className="min-w-0">
            <span className="block text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'La personne à contacter' : 'The person to contact'}</span>
            <span className="block truncate font-medium text-[#ECECEC]" style={{ fontSize: 15 }}>{name}</span>
            {contact.title ? <span className="block truncate text-[#AFAFAF]" style={{ fontSize: 13 }}>{contact.title}</span> : null}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {contact.linkedinUrl ? <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn" className={icon}><Linkedin size={15} /></a> : null}
          {contact.email ? <a href={`mailto:${contact.email}`} aria-label={contact.email} title={contact.email} className={icon}><Mail size={15} /></a> : null}
          {searching && !phone ? <span className="mr-1 text-[#8E8E8E]" style={{ fontSize: 12 }} title={stepDebug} role="status">{stepText}</span>
            : phoneMissed && !phone ? <span className="mr-1 text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'Aucun téléphone trouvé' : 'No phone found'}</span> : null}
          {phone ? <a href={`tel:${phone}`} aria-label={phone} title={phone} className={icon}><Phone size={15} /></a>
            : <button type="button" onClick={() => void findPhone()} disabled={searching} aria-label={fr ? 'Trouver le téléphone' : 'Find the phone number'} title={searching ? stepDebug : (fr ? 'Trouver le téléphone (1 crédit FullEnrich par essai, 10 si trouvé)' : 'Find the phone number (uses FullEnrich credits)')} className={icon}>
                {searching ? <Loader2 size={15} className="animate-spin" /> : <Phone size={15} />}
              </button>}
        </div>
      </section>
    );
  }
  return (
    <section aria-label={fr ? 'Contact recommandé' : 'Recommended contact'} className="flex items-start gap-5 border-b border-white/[0.06] px-6 py-5">
      {avatar(96, 32)}
      <div className="min-w-0 flex-1">
        <p className="text-[#8E8E8E]" style={{ fontSize: 13 }}>{fr ? 'La personne à contacter' : 'The person to contact'}</p>
        <h3 className="font-medium text-[#ECECEC]" style={{ fontSize: 20, lineHeight: '28px' }}>{name}</h3>
        {contact.title ? <p className="text-[#D0D0D0]" style={{ fontSize: 15 }}>{contact.title}</p> : null}
        {recommendation.summary ? <p className="mt-2 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{recommendation.summary}</p> : null}
        {recommendation.reason ? <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '20px' }}>{fr ? 'Pourquoi' : 'Why'}: {recommendation.reason}</p> : null}
        <div className="mt-3 flex flex-wrap gap-2" style={{ fontSize: 13 }}>
          {contact.linkedinUrl ? <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className={link}><Linkedin size={13} />LinkedIn</a> : null}
          {contact.email ? <a href={`mailto:${contact.email}`} className={link}><Mail size={13} />{contact.email}</a> : null}
          {phone ? <a href={`tel:${phone}`} className={link}><Phone size={13} />{phone}</a> : (
            <button type="button" onClick={() => void findPhone()} disabled={searching} title={searching ? stepDebug : undefined} className={`${link} disabled:opacity-80`}>
              {searching ? <Loader2 size={13} className="animate-spin" /> : <Phone size={13} />}
              {searching ? <span role="status" style={{ fontSize: 'inherit' }}>{stepText}</span>
                : phoneMissed ? (fr ? 'Aucun téléphone trouvé · réessayer' : 'No phone found · try again')
                  : (fr ? 'Trouver le téléphone' : 'Find phone')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
